import { FC, useMemo } from 'react';

// Third-party
import { useTranslation } from 'react-i18next';
import { alpha, useTheme } from '@mui/material';
import moment from 'moment';

// Hooks
import { TCustomTheme } from '@mgm/common/theme';
import { getTimezone } from '@mgm/common/utils';
import { useAppSelector } from '../../../../hooks/useAppStore';

// Redux-toolkit
import { getTasklists, getTasklistSelectedLocationId } from '../../../../store/tasklists/selectors';

// Assets
import { ReactComponent as TasksIcon } from '../../../../static/icons/tasklists/ic_tasks.svg';
import { ReactComponent as TodayIcon } from '../../../../static/icons/ic_today.svg';
import { ReactComponent as ListIcon } from '../../../../static/icons/tasklists/ic_list.svg';

// Data

// Types

type TTasklistItem = {
  title: string;
  value: string | number;
  icon: FC,
  iconBg: string
  cardBg: string,
  cardColor: string
};

export function useTasklists() {
  const { t } = useTranslation('tasklists');
  const tasklists = useAppSelector(getTasklists);
  const theme = useTheme() as TCustomTheme;
  const tasklistSelectedLocationId = useAppSelector(getTasklistSelectedLocationId);

  const totalData = useMemo(() => {
    let filteredTasklists = tasklists;

    // If tasklistSelectedLocationId is greater than 0, filter tasklists by location
    if (tasklistSelectedLocationId > 0) {
      filteredTasklists = tasklists.filter((tasklist) => tasklist.locations.some((location) => location.id === tasklistSelectedLocationId));
    }

    // Count the total number of tasks
    let count = 0;
    filteredTasklists.forEach((tasklist) => {
      count += tasklist.tasks.length;
    });

    // Filter the tasklists for today
    const todayTasklists = filteredTasklists.filter((tasklist) => {
      const dateInTimeZone = moment.tz(tasklist.date, getTimezone());
      const currentDateInTimeZone = moment.tz(getTimezone());
      return dateInTimeZone.isSame(currentDateInTimeZone, 'day');
    });

    return { newTasks: count, today: todayTasklists.length, tasklists: filteredTasklists.length };
  }, [tasklists, tasklistSelectedLocationId]);

  const getTotalItems = useMemo(() => {
    const items = [
      {
        title: t('newTasks'),
        value: totalData.newTasks,
        icon: TasksIcon,
        cardBg: theme.palette.primary.lighter,
        cardColor: theme.palette.primary.darker,
        iconBg: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0)} 0%, ${alpha(
          theme.palette.primary.dark,
          0.24,
        )} 100%)`,
      },
      {
        title: t('dueToday'),
        value: totalData.today,
        icon: TodayIcon,
        cardColor: theme.palette.info.darker,
        cardBg: theme.palette.info.lighter,
        iconBg: `linear-gradient(135deg, ${alpha(theme.palette.info.dark, 0)} 0%, ${alpha(
          theme.palette.info.dark,
          0.24,
        )} 100%)`,
      },
      {
        title: t('tasklists'),
        cardColor: theme.palette.warning.darker,
        cardBg: theme.palette.warning.lighter,
        value: totalData.tasklists,
        icon: ListIcon,
        iconBg: `linear-gradient(135deg, ${alpha(theme.palette.warning.dark, 0)} 0%, ${alpha(
          theme.palette.warning.dark,
          0.24,
        )} 100%)`,
      },
    ];

    return items as Array<TTasklistItem>;
  }, [totalData]);

  return {
    getTotalItems,
  };
}
